<script setup lang="ts">
import { useAppStore, type Toast } from '@/stores/app';

interface Props {
  toasts: Toast[];
}

defineProps<Props>();

const appStore = useAppStore();

function removeToast(id: number) {
  const index = appStore.toasts.findIndex((t) => t.id === id);
  if (index !== -1) {
    appStore.toasts.splice(index, 1);
  }
}

function getToastClasses(type: Toast['type']): string {
  const classes: Record<Toast['type'], string> = {
    success: 'bg-white border-l-4 border-l-green-500 text-gray-900',
    error: 'bg-white border-l-4 border-l-red-500 text-gray-900',
    info: 'bg-white border-l-4 border-l-blue-500 text-gray-900',
    warning: 'bg-white border-l-4 border-l-amber-500 text-gray-900',
  };
  return classes[type] ?? classes.info;
}

function getIconColor(type: Toast['type']): string {
  const colors: Record<Toast['type'], string> = {
    success: 'text-green-500',
    error: 'text-red-500',
    info: 'text-blue-500',
    warning: 'text-amber-500',
  };
  return colors[type] ?? colors.info;
}
</script>

<template>
  <div class="fixed top-4 right-4 z-50 space-y-2 pointer-events-none"
       role="region"
       aria-live="polite"
       aria-label="Notifications">
    <transition-group name="toast">
      <div v-for="toast in toasts" :key="toast.id"
        role="alert"
        :class="[
          'pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl shadow-elevated border border-gray-100 transition-all max-w-md animate-in',
          getToastClasses(toast.type)
        ]">
        <!-- Icon -->
        <svg v-if="toast.type === 'success'" aria-hidden="true" :class="['w-5 h-5 flex-shrink-0', getIconColor(toast.type)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <svg v-else-if="toast.type === 'error'" aria-hidden="true" :class="['w-5 h-5 flex-shrink-0', getIconColor(toast.type)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <svg v-else-if="toast.type === 'warning'" aria-hidden="true" :class="['w-5 h-5 flex-shrink-0', getIconColor(toast.type)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
        </svg>
        <svg v-else aria-hidden="true" :class="['w-5 h-5 flex-shrink-0', getIconColor(toast.type)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>

        <!-- Message -->
        <span class="flex-1 text-sm font-medium">{{ toast.message }}</span>

        <!-- Close -->
        <button @click="removeToast(toast.id)"
          aria-label="Dismiss notification"
          class="flex-shrink-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors">
          <svg aria-hidden="true" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </transition-group>
  </div>
</template>
